// src/data/box1Guided.js
//
// RULING 261 (2026-09-29). BOX 1 ASKS THE KIND OF CLAIM FIRST, THEN SHORT QUESTIONS FOR THAT KIND.
// Bob: "I approve, however, i want us to be able to reverse it exactly if I dont like it"; then, on the
// Quick File screen review, "proceed as you stated".
//
// EXACT REVERSAL. Everything 261 adds is gated on BOX1_GUIDED below.
//   - false: QuickFile renders today's Box 1, Box 3, Box 5 and Presentment exactly as before; the panels
//     receive no new props; the claim bag never carries claimKind / box1Answers / statementOfClaim, and
//     the payload builder writes a field only when the bag has it, so no claim changes shape.
//   - The whole of 261 is one commit citing 261 only; `git revert` of it removes it.
//   - "withdraw 261" = set BOX1_GUIDED to false (or revert the commit). Nothing else depends on it.
//
// Record 600 (claim categories, Bob 10/7): the seven kinds became the 29 carrier violation categories. Gated on
// CLAIM_CATEGORIES_600 below: false = the seven 261 kinds exactly as before. The bar warning (claimBars.js) is
// shown inside the BOX1_GUIDED block, so BOX1_GUIDED false still gives the pre-261 screen.
//
// Pure module: no React, no Firestore.

export const BOX1_GUIDED = true;

const s = (v) => (v == null ? '' : String(v).trim());

// Step 1 - the kind of claim (Bob approved the list, 2026-09-29). `rules` narrows rule matching to
// rule labels that name the kind; when nothing in the agreement's list matches, the full list is used
// (never an empty list - a narrowing that finds nothing must not stop the analysis).
// Record 600 (claim categories, Bob 10/7): the seven 261 kinds are KEPT here, unchanged, as KINDS_261 - they are
// what CLAIM_KINDS is when CLAIM_CATEGORIES_600 is false (exact reversal of 600, same pattern as BOX1_GUIDED).
export const KINDS_261 = [
  { id: 'vacation', label: 'Vacation', rules: /vacation/i, set: 'vacation', template: 'VAC-seniority-order' },
  { id: 'overtime', label: 'Overtime or extra work', rules: /overtime|extra (work|service)|call/i, set: 'overtime' },
  { id: 'position', label: 'Position or bid award', rules: /bid|bulletin|award|position|assign|seniority/i, set: 'position', template: 'POS-award-of-position' },
  { id: 'scope', label: 'Work taken by others', rules: /scope|work|perform|non-?agreement|outsid|craft/i, set: 'scope' },
  { id: 'pay', label: 'Pay or rate', rules: /pay|rate|wage|compensat|guarantee|holiday|differential/i, set: 'pay' },
  { id: 'discipline', label: 'Discipline', rules: /disciplin|investigat|hearing|dismiss|suspen/i, set: 'discipline' },
  { id: 'other', label: 'Something else', rules: null, set: 'default' },
];

// Record 600 (claim categories, Bob 10/7). Bob approved (2026-10-07) replacing the seven kinds with the carrier
// violation categories of data/YCP_CARRIER_VIOLATION_CATEGORIES_2026-10-07.md section 1 (lines 18-47): #1-#22 and
// #24-#30, 29 options. #23 "Carrier missed a claim time limit" is NOT a kind - it is a default on a claim already
// filed and is built separately as a filed-claim watch.
//   - Labels are the document's "Label (what the member sees)" column, verbatim; `n` is the document's number and
//     `src` the line it is on (scripts/test-claim-categories.mjs re-reads every label from the document).
//   - IDS: the seven 261 ids are kept as the ids of the closest new category, so a saved draft's claimKind and
//     penaltyClaim.js KIND_TEMPLATE (vacation, position) keep working with no migration: vacation -> #13 Vacation,
//     overtime -> #2 Called out of order, position -> #5 Bulletin, bid & award, scope -> #1 Work taken by others,
//     pay -> #11 Pay, rate & differential, discipline -> #21 Discipline, other -> #30 Something else. The other 22
//     categories have new ids.
//   - `set`: each category reuses the closest existing question set (GUIDED_QUESTION_SETS below); where none fits,
//     the generic set ('default'). No question set is new.
//   - `rules`: matched against the rule LABELS of the claim's agreement (AGREEMENT_RULES in agreementData.js) by the
//     category's keywords. Every regex was checked against those labels; the ones that match no label anywhere are
//     listed in scripts/test-claim-categories.mjs (NO_LABEL_MATCH) - they fall back to the full list, as always.
//   - `group`: one of six headings so 29 chips read as a short list on the screen.
export const CLAIM_CATEGORIES_600 = true;
const DOC = 'data/YCP_CARRIER_VIOLATION_CATEGORIES_2026-10-07.md:';
// Record 603 (Bob 10/8): the order the two-step picker shows the six group buttons in.
export const CLAIM_KIND_GROUPS = ['Work & assignments', 'Time & pay', 'Seniority & jobs', 'Leave & benefits', 'Discipline & treatment', 'Rights & procedure'];
const W = CLAIM_KIND_GROUPS[0], T = CLAIM_KIND_GROUPS[1], J = CLAIM_KIND_GROUPS[2], L = CLAIM_KIND_GROUPS[3], D = CLAIM_KIND_GROUPS[4], R = CLAIM_KIND_GROUPS[5];
export const CATEGORIES_600 = [
  { n: 1, id: 'scope', label: 'Work taken by others (outside the yardmaster craft)', group: W, set: 'scope', rules: /scope|jurisdiction|classification|perform|non-?agreement|outsid|craft/i },
  { n: 2, id: 'overtime', label: 'Called out of order / runaround / overtime not offered', group: W, set: 'overtime', rules: /overtime|\bcalls?\b|extra|unassigned|hold-?down/i },
  { n: 3, id: 'held_over', label: 'Held over / overtime or double shift not paid right', group: T, set: 'pay', rules: /overtime|held over|basic day|basis of pay|\bcalls?\b|turnover/i },
  { n: 4, id: 'rest_days', label: 'Rest days, work week & starting time', group: W, set: 'default', rules: /rest day|work week|starting time|assignment beginning|hours of service/i },
  { n: 5, id: 'position', label: 'Bulletin, bid & award not followed', group: J, set: 'position', template: 'POS-award-of-position', rules: /bulletin|\bbid|award|re-?advertis|position transfer|selection/i },
  { n: 6, id: 'abolished', label: 'Job abolished / force reduction without required notice', group: J, set: 'default', rules: /abolish|reduction|forces?\b|furlough/i },
  { n: 7, id: 'displacement', label: 'Displacement (bumping) rights denied', group: J, set: 'default', rules: /displac|exercise of seniority|bump/i },
  { n: 8, id: 'seniority', label: 'Seniority date, roster & district (incl. BN protected / ROA)', group: J, set: 'default', rules: /seniority|roster|protest|appointment|\bROA\b|district/i },
  { n: 9, id: 'extra_board', label: 'Extra board / extra list & guarantee', group: W, set: 'overtime', rules: /extra (board|list)|extra \/|unassigned|guarantee|GREB|hold-?down/i },
  { n: 10, id: 'vacancy', label: 'Relief jobs & temporary vacancies', group: W, set: 'overtime', rules: /relief|vacanc|temporary|borrow/i },
  { n: 11, id: 'pay', label: 'Pay, rate & differential', group: T, set: 'pay', rules: /pay|rate|wage|compensat|differential|COLA/i },
  { n: 12, id: 'holiday', label: 'Holiday', group: T, set: 'pay', rules: /holiday/i },
  { n: 13, id: 'vacation', label: 'Vacation', group: L, set: 'vacation', template: 'VAC-seniority-order', rules: /vacation/i },
  { n: 14, id: 'sick_personal', label: 'Sick & personal leave days', group: L, set: 'pay', rules: /sick|personal leave|FMLA/i },
  { n: 15, id: 'bereavement', label: 'Bereavement, jury duty & court/witness', group: L, set: 'pay', rules: /bereavement|jury|court|witness/i },
  { n: 16, id: 'leave_return', label: 'Leave of absence & return to work', group: L, set: 'default', rules: /leave of absence|military|return/i },
  { n: 17, id: 'meal', label: 'Meal period', group: T, set: 'pay', rules: /meal/i },
  { n: 18, id: 'travel', label: 'Travel, deadhead, auto & expenses', group: T, set: 'pay', rules: /travel|deadhead|auto|expense|mileage/i },
  { n: 19, id: 'training', label: 'Training, qualification & promotion', group: J, set: 'default', rules: /train|qualif|promot|probation/i },
  { n: 20, id: 'medical', label: 'Medical / physical disqualification', group: D, set: 'default', rules: /medical|physical|doctor|incapacitat|occupational health/i },
  { n: 21, id: 'discipline', label: 'Discipline - charge, hearing & time limits', group: D, set: 'discipline', rules: /disciplin|investigat|hearing|dismiss|suspen/i },
  { n: 22, id: 'unjust', label: 'Unjust treatment (non-discipline)', group: D, set: 'default', rules: /unjust|grievance/i },
  { n: 24, id: 'records', label: 'Records & information not furnished', group: R, set: 'default', rules: /record|information|furnish|transcript|roster error/i },
  { n: 25, id: 'changes', label: 'Changes made without required notice or agreement', group: R, set: 'default', rules: /notice|change|programming|re-?advertis/i },
  { n: 26, id: 'discrimination', label: 'Discrimination / harassment', group: D, set: 'default', rules: /discriminat|harass/i },
  { n: 27, id: 'protection', label: 'Job protection & protective benefits', group: J, set: 'pay', rules: /protect|merger|separation|relocat/i },
  { n: 28, id: 'union', label: 'Union representation & committee rights', group: R, set: 'default', rules: /representat|committee|union|dues/i },
  { n: 29, id: 'benefits', label: 'Benefits (health & welfare, supplemental sickness, off-track injury, pension/401k)', group: L, set: 'default', rules: /benefit|health (and|&) welfare|pension|401|insurance|supplemental sickness|off-track/i },
  { n: 30, id: 'other', label: 'Something else', group: R, set: 'default', rules: null },
].map(k => ({ ...k, src: DOC + (17 + k.n) }));

export const CLAIM_KINDS = CLAIM_CATEGORIES_600 ? CATEGORIES_600 : KINDS_261;
export function claimKind(id) { return CLAIM_KINDS.find(k => k.id === id) || null; }

// Record 600: the chips under their headings, in heading order and the document's order within a heading.
// With CLAIM_CATEGORIES_600 false: one untitled group holding the seven 261 kinds, which is the 261 screen.
export function claimKindGroups() {
  if (!CLAIM_CATEGORIES_600) return [{ heading: null, kinds: CLAIM_KINDS }];
  return CLAIM_KIND_GROUPS.map(h => ({ heading: h, kinds: CLAIM_KINDS.filter(k => k.group === h) }));
}

// Record 603 (Bob 10/8: break the 29 into smaller groups): THE TWO-STEP PICKER. Step one is the six group buttons
// (CLAIM_KIND_GROUPS, in that order); step two is only the chosen group's categories, in the document's order.
// Pure - QuickFile.js renders it. With CLAIM_CATEGORIES_600 false there are no groups (kindsInGroup -> [],
// groupOfKind -> null) and claimKindPicker says twoStep false, so the screen keeps the one untitled row of the
// seven 261 kinds from claimKindGroups() above, unchanged.
export function kindsInGroup(group) {
  if (!CLAIM_CATEGORIES_600 || !CLAIM_KIND_GROUPS.includes(group)) return [];
  return CLAIM_KINDS.filter(k => k.group === group);
}
export function groupOfKind(id) {
  if (!CLAIM_CATEGORIES_600) return null;
  const k = claimKind(id);
  return k ? k.group : null;
}
// chosenGroup: null/undefined = no group pressed on this screen yet, so a chosen category (a resumed draft) opens
// its own group; '' = "Change group" was pressed (the six buttons show again, the category is kept); a group name =
// that group is open. An unknown name opens nothing.
export function claimKindPicker(chosenGroup, kindId) {
  if (!CLAIM_CATEGORIES_600) return { twoStep: false, groups: [], open: null, kinds: CLAIM_KINDS };
  const open = (chosenGroup === null || chosenGroup === undefined)
    ? groupOfKind(kindId)
    : (CLAIM_KIND_GROUPS.includes(chosenGroup) ? chosenGroup : null);
  return { twoStep: true, groups: CLAIM_KIND_GROUPS, open, kinds: open ? kindsInGroup(open) : [] };
}

export function narrowRules(kindId, availableRules) {
  const all = Array.isArray(availableRules) ? availableRules : [];
  const k = claimKind(kindId);
  if (!k || !k.rules) return all;
  const hit = all.filter(r => k.rules.test(typeof r === 'string' ? r : s(r && (r.rule || r.label || r.title))));
  return hit.length ? hit : all;
}

// Step 2 - short questions per kind. Ids beginning q_ are shared with Box 3's existing renderer
// (datetime / text); the availability answer is offered ONLY under q_available (item 3 of the
// 2026-09-29 screen review: it had sat under "What is your seniority date").
const AVAILABLE = { id: 'q_available', q: 'Were you ready, willing, able and available to perform the work?' };
export const GUIDED_QUESTION_SETS = {
  vacation: [
    { id: 'q_vac_weeks', q: 'Which week or weeks did you ask for?' },
    { id: 'q_vac_request', q: 'When did you put in your request, and to whom?' },
    { id: 'q_junior', q: 'Who got the week instead? (name, roster number)' },
    { id: 'q_manager', q: 'Who made the decision? (name and title)' },
    { id: 'q_vac_reason', q: 'Were you told a reason? What was it?' },
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
  // Bob, 2026-10-17, on the Amtrak test claim: the set left out the facts a carrier denies a claim for - the job, how
  // it came open, the tour and yard, the overtime list, and where each man stands on the roster. The other employee is
  // named as a fact: "not guilty of anything but for accepting his orders" - the claim is against the Carrier's call.
  // Plan: data/YCP_PLAN_OVERTIME_CLAIM_FACTS_2026-10-17.md, section A.
  overtime: [
    { id: 'q_datetime', q: 'What was the date and time of the overtime or extra work?' },
    { id: 'q_ot_job', q: 'Which job was it? Give the job symbol, the tour or starting time, and the yard or location.' },
    { id: 'q_ot_vacancy', q: 'How and when did the job come open, if you know? (vacation, sick, personal leave, vacancy, new or extra job)' },
    { id: 'q_ot_rate', q: 'Was it worked at the overtime rate, or filled as a straight-time vacancy?' },
    { id: 'q_junior', q: 'Who was called to work it? Give his name, roster number and seniority date.' },
    { id: 'q_manager', q: 'Who made the call or assignment? (name and title)' },
    { id: 'q_seniority', q: 'What is your seniority date and standing on the roster?' },
    { id: 'q_ot_list', q: 'Were you on the overtime or extra list, or had you asked in writing to be called? Since when?' },
    AVAILABLE,
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
  position: [
    { id: 'q_pos_bulletin', q: 'Which position, and when was it bulletined?' },
    { id: 'q_pos_bid', q: 'When did you bid, and how (in writing, electronically)?' },
    { id: 'q_junior', q: 'Who was awarded the position? (name, roster number)' },
    { id: 'q_seniority', q: 'What is your seniority date and standing on the roster?' },
    { id: 'q_manager', q: 'Who made the award? (name and title)' },
  ],
  scope: [
    { id: 'q_datetime', q: 'What was the date and time the work was performed?' },
    { id: 'q_scope_work', q: 'What work was performed? (the specific duties)' },
    { id: 'q_junior', q: 'Who performed it? (name, title or craft)' },
    { id: 'q_manager', q: 'Who directed it? (name and title)' },
    AVAILABLE,
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
  pay: [
    { id: 'q_pay_dates', q: 'Which pay period or dates are short?' },
    { id: 'q_pay_paid', q: 'What were you paid, and what should you have been paid?' },
    { id: 'q_pay_reported', q: 'When and to whom did you report it?' },
  ],
  discipline: [
    { id: 'q_datetime', q: 'What was the date of the charge or the incident?' },
    { id: 'q_disc_charge', q: 'What were you charged with, and by whom?' },
    { id: 'q_disc_hearing', q: 'Has a hearing been scheduled? When?' },
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
  default: [
    { id: 'q_datetime', q: 'What was the exact date and time of the incident?' },
    { id: 'q_manager', q: 'What is the name and title of the manager involved?' },
    { id: 'q_junior', q: 'Were any other employees directly involved? Names and roster numbers?' },
    AVAILABLE,
    { id: 'q_witnesses', q: 'Were any witnesses present? If so, who?' },
  ],
};
export function guidedQuestions(kindId) {
  const k = claimKind(kindId);
  return GUIDED_QUESTION_SETS[(k && k.set) || 'default'] || GUIDED_QUESTION_SETS.default;
}

// The standard availability answer (SME: Bob Keeley), offered only under q_available.
export const AVAILABILITY_ANSWER = 'I was at all times ready, willing, able, available, qualified, and senior to the employee who was selected to perform the work in my place.';
export function guidedSuggestion(id) { return id === 'q_available' ? AVAILABILITY_ANSWER : null; }

// The facts reminder shown under Box 1.
export const FACTS_LINE = 'Say what happened, what you saw, and what you were told, and by whom. This goes to the Carrier.';

// What's still missing - who, what, when, where, what you should have gotten. Information only.
export function missingChecklist(claim, kindId) {
  const c = claim || {};
  const a = c.box1Answers || {};
  const has = (...vals) => vals.some(v => s(v) !== '');
  return [
    // Record 600: by question set, not by id - every category asked the pay questions (holiday, meal, travel ...) has no
    // other employee to name. With the 261 list only 'pay' has that set, so this is the 261 rule unchanged.
    { id: 'who', label: 'Who - the other employee, and who decided', ok: (claimKind(kindId) || {}).set === 'pay' || (has(c.violatorName, a.q_junior) && has(a.q_manager, a.q_disc_charge)) },
    { id: 'what', label: 'What happened', ok: has(c.description, a.q_scope_work, a.q_vac_weeks, a.q_pos_bulletin, a.q_pay_paid, a.q_disc_charge, a.q_ot_job) },
    { id: 'when', label: 'When', ok: has(c.incidentDate) },
    { id: 'where', label: 'Where', ok: has(c.eventLocation, c.workLocation, a.q_ot_job) },
    { id: 'owed', label: 'What you should have gotten', ok: has(a.q_vac_weeks, a.q_available, a.q_pay_paid, a.q_pos_bid) },
  ];
}

// Item 6 of the screen review: "Text of ARTICLE 10 - ... - ARTICLE 10 - ... (CSX ...)" printed the rule
// twice. The guided screen names it once. Only the duplicated tail is removed; nothing is added.
export function cleanExhibitLabel(desc) {
  const t = s(desc);
  const m = /^Text of (.+?) - (.+?)( \(.+\))?$/.exec(t);
  if (m && (m[2] === m[1] || m[2].startsWith(m[1]))) return 'Text of ' + m[1];
  const m2 = /^Text of (.+?) \((.+)\)$/.exec(t);
  if (m2) return 'Text of ' + m2[1];
  return t;
}

// Item 2: the Presentment story boxes that repeat Box 1 are not shown on the guided screen. The story
// is told once (Box 1 + questions -> Statement of Claim, ruling 258); the reasons are the form's fixed
// payment paragraph (ruling 257).
export const PRESENTMENT_OMIT = ['detailsOfWork', 'reasonsSupporting'];
// Item 5: "Tour of duty" is not asked for claims printed on Bob's Penalty Time Claim form - he deleted
// "Tour/" from it (2026-09-29). Time of Violation stays.
export const PAYABLE_OMIT = ['claimantTourOfDuty'];
